import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the app and exposes:
 *   - user            : { email, display_name, picture, hasPaid, ... } | null
 *   - loading         : true while the initial /api/auth/me check is in-flight
 *   - signIn()        : redirects to Emergent Google OAuth
 *   - signOut()       : clears the session cookie + local state
 *   - refresh()       : re-fetches /api/auth/me
 *   - exchangeSession : POSTs a session_id (from URL fragment) → /api/auth/session
 *   - verifyPurchase  : POST /api/stripe/verify; returns the updated hasPaid bool
 *
 * Session cookies are httpOnly + samesite=none + secure (set by the backend),
 * so we always pass credentials: 'include' on auth-related requests.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!API) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const r = await fetch(`${API}/api/auth/me`, {
        credentials: "include",
      });
      if (!r.ok) {
        setUser(null);
        setLoading(false);
        return null;
      }
      const data = await r.json();
      setUser(data.user || null);
      setLoading(false);
      return data.user || null;
    } catch (e) {
      setUser(null);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (
      typeof window !== "undefined" &&
      window.location.hash &&
      window.location.hash.includes("session_id=")
    ) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signIn = useCallback(() => {
    if (typeof window === "undefined" || !API) return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  }, []);

  const signOut = useCallback(async () => {
    if (!API) return;
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      /* ignore */
    }
    setUser(null);
  }, []);

  const exchangeSession = useCallback(
    async (sessionId) => {
      if (!API || !sessionId) return null;
      const r = await fetch(`${API}/api/auth/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setUser(data.user || null);
      setLoading(false);
      return data.user || null;
    },
    []
  );

  const verifyPurchase = useCallback(async () => {
    if (!API) return null;
    const r = await fetch(`${API}/api/stripe/verify`, {
      method: "POST",
      credentials: "include",
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.detail || `HTTP ${r.status}`);
    }
    const data = await r.json();
    // Pull the freshly-updated user so the UI reflects hasPaid=true.
    if (data && data.hasPaid) {
      await refresh();
    }
    return data;
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      refresh,
      exchangeSession,
      verifyPurchase,
    }),
    [user, loading, signIn, signOut, refresh, exchangeSession, verifyPurchase]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
